import { useMemo } from 'react';
import Card from '../../components/card/Card';
import P from '../../components/texts/p/P';
import H from '../../components/texts/h/H';
import Badge from '../../components/badge/Badge';
import Span from '../../components/texts/span/Span';
import { APPLICATION_STATUS } from '../../utils/Constants';

export interface ApprovalHistoryTimelineItem {
  employeeName: string;
  roleLabel: string;
  statusLabel: string;
  description: string | null;
  updatedAt: string;
  cycleId?: number;
}

interface ApprovalHistoryTimelineProps {
  items: ApprovalHistoryTimelineItem[];
  /**
   * Ciclo activo (usualmente `quorum.cycleId`). Permite resaltar el ciclo actual
   * y atenuar los anteriores. Si no se pasa, se asume que el último ciclo presente
   * en `items` es el actual.
   */
  currentCycleId?: number;
}

/**
 * Task 50111 — MEMBER_QUORUM (Innova): timeline de aprobaciones agrupado por
 * `cycleId`. Cada ciclo se renderiza como un bloque visual independiente para
 * dejar claro que las aprobaciones que ocurrieron antes de una transición
 * COM → PEN → COM ya no cuentan para el conteo del ciclo actual.
 *
 * Diferenciación visual de acciones:
 *   - "En comité" / aprobado por miembro → success
 *   - Pendiente / cambios solicitados → warning
 *   - Rechazado / declinado → danger
 *
 * Convenciones:
 *   - Si el backend no envía `cycleId` (compatibilidad), se asume ciclo 1.
 *   - El ciclo actual va arriba; los anteriores van debajo en orden descendente,
 *     atenuados, para que el revisor del comité vea primero lo más relevante.
 */
const ApprovalHistoryTimeline: React.FC<ApprovalHistoryTimelineProps> = ({
  items,
  currentCycleId,
}) => {
  const groupedByCycle = useMemo(() => {
    const map = new Map<number, ApprovalHistoryTimelineItem[]>();
    items.forEach((item) => {
      const cycle = item.cycleId ?? 1;
      const arr = map.get(cycle) ?? [];
      arr.push(item);
      map.set(cycle, arr);
    });
    return Array.from(map.entries()).sort((a, b) => b[0] - a[0]);
  }, [items]);

  const effectiveCurrent = useMemo(() => {
    if (typeof currentCycleId === 'number') return currentCycleId;
    return groupedByCycle[0]?.[0] ?? 1;
  }, [currentCycleId, groupedByCycle]);

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <Card width='100%' display='grid' gap='16px'>
      {groupedByCycle.map(([cycleId, cycleItems]) => {
        const isCurrent = cycleId === effectiveCurrent;
        return (
          <Card
            key={cycleId}
            variant='neutral'
            modifier={isCurrent ? 'inverted' : 'weak'}
            spacing={'3'}
            data-testid={`cycle-block-${cycleId}`}
          >
            <Card display='flex' alignItems='center' gap='8px' margin={{ bottom: '2' }}>
              <H type='4' variant='neutral-1' withMargin={false} weight='bold'>
                Ciclo {cycleId}
              </H>
              {isCurrent ? (
                <Badge type='text' variant='success' size='sm'>Actual</Badge>
              ) : (
                <Badge type='text' variant='neutral' size='sm'>Histórico</Badge>
              )}
              <Span type='5' variant='neutral'>
                {cycleItems.length} aprobación/es
              </Span>
            </Card>
            <Card display='grid' gap='8px'>
              {cycleItems.map((item, idx) => (
                <Card
                  key={`${cycleId}-${idx}`}
                  display='flex'
                  alignItems='center'
                  gap='12px'
                  data-testid={`cycle-${cycleId}-row-${idx}`}
                >
                  <Span type='5' variant='neutral'>{item.updatedAt}</Span>
                  <Span type='5' variant='neutral' weight='bold'>{item.employeeName}</Span>
                  <Span type='5' variant='neutral'>({item.roleLabel})</Span>
                  <Badge
                    type='text'
                    variant={statusToVariant(item.statusLabel)}
                    size='sm'
                  >
                    {item.statusLabel}
                  </Badge>
                  {item.description && (
                    <P type='5' variant='neutral' withMargin={false}>
                      “{item.description}”
                    </P>
                  )}
                </Card>
              ))}
            </Card>
          </Card>
        );
      })}
    </Card>
  );
};

/**
 * Mapea el statusLabel devuelto por ms-application al variant del Badge. La lógica
 * es defensiva: si el backend cambia un label esperamos caer al neutral en lugar
 * de romper el render.
 */
const statusToVariant = (statusLabel: string): string => {
  if (!statusLabel) return 'neutral';
  const lower = statusLabel.toLowerCase();
  if (lower.includes(APPLICATION_STATUS.rejected.label.toLowerCase()) ||
      lower.includes('declin') || lower.includes('rechaz')) {
    return 'danger';
  }
  if (lower.includes(APPLICATION_STATUS.pending.label.toLowerCase()) ||
      lower.includes('cambio') || lower.includes('pend')) {
    return 'warning';
  }
  if (lower.includes('aprob') || lower.includes('comité') || lower.includes('comite')) {
    return 'success';
  }
  return 'info';
};

export default ApprovalHistoryTimeline;
